import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

import { formatNotificationAge } from '@/src/features/notifications/notification-time';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { formatDate } from '@/src/utils/dates';

export function RelativeTimeTooltip({
  accessibilityLabelPrefix = 'Time',
  buttonPressedStyle,
  buttonStyle,
  containerStyle,
  textStyle,
  tooltipStyle,
  tooltipTextStyle,
  value,
}: {
  accessibilityLabelPrefix?: string;
  buttonPressedStyle?: StyleProp<ViewStyle>;
  buttonStyle?: StyleProp<ViewStyle>;
  containerStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  tooltipStyle?: StyleProp<ViewStyle>;
  tooltipTextStyle?: StyleProp<TextStyle>;
  value: unknown;
}) {
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const relativeTime = formatNotificationAge(value);
  const fullTime = formatDate(value);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const hideTooltip = useCallback(() => {
    clearTimer();
    setVisible(false);
  }, [clearTimer]);

  const showTooltip = useCallback(() => {
    if (!relativeTime) {
      return;
    }

    clearTimer();
    setVisible(true);
    timeoutRef.current = setTimeout(() => {
      setVisible(false);
      timeoutRef.current = null;
    }, 2600);
  }, [clearTimer, relativeTime]);

  useEffect(() => {
    setVisible(false);
    clearTimer();
  }, [clearTimer, value]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  if (!relativeTime) {
    return null;
  }

  return (
    <View style={[styles.container, containerStyle]}>
      <Pressable
        accessibilityHint="Shows the full date and time."
        accessibilityLabel={`${accessibilityLabelPrefix} ${fullTime}`}
        accessibilityRole="button"
        onHoverIn={Platform.OS === 'web' ? showTooltip : undefined}
        onHoverOut={Platform.OS === 'web' ? hideTooltip : undefined}
        onPress={(event) => {
          event.stopPropagation();
          showTooltip();
        }}
        style={({ pressed }) => [
          styles.button,
          buttonStyle,
          pressed ? buttonPressedStyle : null,
        ]}
      >
        <Text style={textStyle}>{relativeTime}</Text>
      </Pressable>
      {visible ? (
        <View pointerEvents="none" style={[styles.tooltip, tooltipStyle]}>
          <Text style={[styles.tooltipText, tooltipTextStyle]}>{fullTime}</Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  button: {
    alignSelf: 'flex-start',
    borderRadius: 6,
    paddingVertical: 1,
  },
  container: {
    alignSelf: 'flex-start',
    position: 'relative',
    zIndex: 35,
  },
  tooltip: {
    backgroundColor: colors.text,
    borderRadius: 6,
    bottom: 25,
    left: 0,
    maxWidth: 240,
    minWidth: 170,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
    position: 'absolute',
    zIndex: 50,
  },
  tooltipText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '800',
    textAlign: 'center',
  },
});
